import { useState, useEffect, useMemo, useCallback } from "react";
import "../css/tablas.css";
import BarraSuperior from "./barra_superior";


const isNumerico = (valor) =>
  valor != null &&
  !isNaN(parseFloat(String(valor))) &&
  isFinite(valor);

const pareceUnaFecha = (texto) =>
  typeof texto === "string" &&
  (
    /^\d{4}-\d{2}-\d{2}/.test(texto) ||
    texto.includes("T") ||
    /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(texto)
  );

const convertirAFecha = (valor) => {
  if (typeof valor !== "string") return new Date(valor);

  const coincideDiaMesAnio = valor.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (coincideDiaMesAnio) {
    const [, dia, mes, anio] = coincideDiaMesAnio;
    return new Date(`${anio}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`);
  }

  const coincide12Horas = valor.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp])\.?[Mm]\.?$/
  );
  if (coincide12Horas) {
    const [, anio, mes, dia, hora, minuto, segundo, meridiano] = coincide12Horas;
    let horas24 = Number(hora) % 12;
    if (meridiano.toLowerCase() === "p") horas24 += 12;
    return new Date(
      Number(anio), Number(mes) - 1, Number(dia),
      horas24, Number(minuto), Number(segundo || 0)
    );
  }

  return new Date(valor);
};

const compararValores = (valorA, valorB, direccion) => {
  if (isNumerico(valorA) && isNumerico(valorB)) {
    return direccion === "asc"
      ? parseFloat(valorA) - parseFloat(valorB)
      : parseFloat(valorB) - parseFloat(valorA);
  }

  if (pareceUnaFecha(valorA) && pareceUnaFecha(valorB)) {
    return direccion === "asc"
      ? +convertirAFecha(valorA) - +convertirAFecha(valorB)
      : +convertirAFecha(valorB) - +convertirAFecha(valorA);
  }

  return direccion === "asc"
    ? String(valorA).localeCompare(String(valorB))
    : String(valorB).localeCompare(String(valorA));
};

const filtrarFilas = (filas, campos, termino) => {
  if (!termino.trim()) return filas;

  const terminoEnMinusculas = termino.toLowerCase();

  return filas.filter((fila) =>
    campos.some((campo) => {
      const valor = fila?.[campo];
      return valor != null && String(valor).toLowerCase().includes(terminoEnMinusculas);
    })
  );
};

const ordenarFilas = (filas, campos, columna, direccion) => {
  if (columna == null) return filas;

  const nombreCampo = campos[columna];

  return [...filas].sort((a, b) =>
    compararValores(
      a?.[nombreCampo] ?? "",
      b?.[nombreCampo] ?? "",
      direccion
    )
  );
};


const renderizarMedia = (url) => {
  const esImagen = /\.(jpg|jpeg|png|gif|svg|webp)/i.test(url);
  const esVideo  = /\.(mp4|mov|avi|wmv|webm)/i.test(url);

  if (esImagen) {
    return (
      <img
        src={url}
        alt="Imagen de la celda"
        width={250}
        loading="lazy"
        style={{ borderRadius: 8 }}
        onError={(e) => (e.target.style.display = "none")}
      />
    );
  }

  if (esVideo) {
    return (
      <video width={720} height={300} controls style={{ borderRadius: 8 }}>
        <source src={url} />
      </video>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer">
      Abrir archivo
    </a>
  );
};

// Fecha con hora ("2026-07-05 12:48 PM", "05/07/2026 12:48:00"): la fecha arriba y la hora debajo.
const FECHA_CON_HORA = /^(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4}),? (\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?: ?[ap]\.? ?m\.?)?)$/i;

const fechaEnDosLineas = (fecha, hora) => (
  <span style={{ whiteSpace: "nowrap" }}>
    {fecha}<br />
    <span style={{ color: "#6b6b70" }}>{hora}</span>
  </span>
);

const renderizarCelda = (valor) => {
  if (typeof valor === "string" && /^https?:\/\//i.test(valor)) {
    return renderizarMedia(valor);
  }
  const fechaHora = typeof valor === "string" && valor.trim().match(FECHA_CON_HORA);
  if (fechaHora) return fechaEnDosLineas(fechaHora[1], fechaHora[2]);
  return valor ?? "NO APLICA";
};

// Títulos cortos en todas las tablas (solo en pantalla; los Excel exportados conservan el nombre completo):
// FECHA DE CREACIÓN → F. CREACIÓN, TIPO DE NOVEDAD → T. NOVEDAD, NOMBRE DEL RESIDENTE → N. RESIDENTE.
const ABREVIATURAS = [
  [/^correo electr[oó]nico$/i, (t) => t.slice(0, 6)],
  [/^(nombre) completo$/i, (t, m) => m[1]],
  [/^(descripci[oó]n) del? .+$/i, (t, m) => m[1]],
  [/^fecha (?:de )?(?!y )(.+)$/i, (t, m) => `F. ${m[1]}`],
  [/^tipo (?:de )?(.+)$/i, (t, m) => `T. ${m[1]}`],
  [/^(?:nombre|n[°º]|n[uú]mero) (?:del? )?(.+)$/i, (t, m) => `N. ${m[1]}`],
];

export const tituloCorto = (titulo) => {
  const t = String(titulo ?? "").trim();
  for (const [regla, corto] of ABREVIATURAS) {
    const m = t.match(regla);
    if (m) return corto(t, m);
  }
  return t;
};

// Columnas que siempre van en una sola línea.
const SIN_SALTO = /^(direcci[oó]n|vivienda|placa)$/i;


const TablaGenerica = ({
  datos                 = [],
  titulos               = [],
  campos                = [],
  elementosPorPagina    = 3000,
  botones               = [],
  columnaOrdenInicial   = null,
  direccionOrdenInicial = "asc",
  mostrarBarraSuperior  = true,
  botonesAlInicio       = false,
  tituloBotones         = "OPCIONES",
  estilosColumnas       = [],
  contenidoExtra        = null,
  renderizadores        = {},
}) => {
  const titulosCortos = titulos.map(tituloCorto);
  const estiloColumna = (indice) =>
    SIN_SALTO.test(String(titulos[indice] ?? "").trim())
      ? { whiteSpace: "nowrap", ...estilosColumnas[indice] }
      : estilosColumnas[indice];

  const [paginaActual,     setPaginaActual]     = useState(1);
  const [terminoBusqueda,  setTerminoBusqueda]  = useState("");
  const [columnaOrden,     setColumnaOrden]     = useState(columnaOrdenInicial);
  const [direccionOrden,   setDireccionOrden]   = useState(direccionOrdenInicial);


  // Cada ventana abre con el buscador vacío. Antes se guardaba en localStorage y
  // lo escrito en una ventana aparecía en las demás, ocultando sus registros.
  useEffect(() => {
    localStorage.removeItem("filtroTablaGenerica");
  }, []);

  useEffect(() => {
    setPaginaActual(1);
  }, [datos.length, terminoBusqueda]);


  const datosFiltradosYOrdenados = useMemo(() => {
    if (!Array.isArray(datos) || !datos.length) return [];

    const filtrados = filtrarFilas(datos, campos, terminoBusqueda);
    return ordenarFilas(filtrados, campos, columnaOrden, direccionOrden);
  }, [datos, terminoBusqueda, campos, columnaOrden, direccionOrden]);

  const datosDeLaPaginaActual = useMemo(() => {
    const totalFilas   = datosFiltradosYOrdenados.length;
    const totalPaginas = Math.max(1, Math.ceil(totalFilas / elementosPorPagina));

    const paginaSegura = Math.min(paginaActual, totalPaginas);
    const indiceInicio = (paginaSegura - 1) * elementosPorPagina;

    return {
      filas:         datosFiltradosYOrdenados.slice(indiceInicio, indiceInicio + elementosPorPagina),
      totalFilas,
      totalPaginas,
      paginaActual:  paginaSegura,
    };
  }, [datosFiltradosYOrdenados, paginaActual, elementosPorPagina]);


  const alHacerClicEnColumna = useCallback((indiceColumna) => {
    setColumnaOrden((columnaActual) => {
      if (columnaActual === indiceColumna) {
        setDireccionOrden((direccionActual) =>
          direccionActual === "asc" ? "desc" : "asc"
        );
        return columnaActual;
      } else {
        setDireccionOrden("asc");
        return indiceColumna;
      }
    });
  }, []);

  const alCambiarPagina = useCallback(
    (numeroPagina) => {
      if (numeroPagina >= 1 && numeroPagina <= datosDeLaPaginaActual.totalPaginas) {
        setPaginaActual(numeroPagina);
      }
    },
    [datosDeLaPaginaActual.totalPaginas]
  );


  const Paginacion = () => {
    if (datosDeLaPaginaActual.totalPaginas <= 1) return null;

    return (
      <nav>
        <ul className="paginacion">
          {Array.from(
            { length: datosDeLaPaginaActual.totalPaginas },
            (_, i) => i + 1
          ).map((numeroPagina) => (
            <li
              key={numeroPagina}
              className={`page-item ${datosDeLaPaginaActual.paginaActual === numeroPagina ? "active" : ""}`}
            >
              <button
                onClick={() => alCambiarPagina(numeroPagina)}
                className="page-link"
              >
                {numeroPagina}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    );
  };


  return (
    <div>
      {mostrarBarraSuperior && <BarraSuperior />}
      <div className="BloqueModulo">

        <div className="Filtros">
          <p>Buscar:</p>
          <input
            type="text"
            value={terminoBusqueda}
            onChange={(e) => setTerminoBusqueda(e.target.value)}
            placeholder="Buscar en la tabla..."
          />
          <p>
            Mostrando {datosDeLaPaginaActual.totalFilas} de {datos.length} registros
          </p>
          {contenidoExtra && (
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              {contenidoExtra}
            </div>
          )}
        </div>

        <div className="ListadoInmuebles">

          <Paginacion />

          <table className="tabla-responsive">

            <thead>
              <tr>
                {titulosCortos.map((titulo, indice) => (
                  <th
                    key={indice}
                    onClick={() => alHacerClicEnColumna(indice)}
                    style={{ cursor: "pointer", ...estiloColumna(indice) }}
                    title={`Ordenar por ${titulos[indice]}`}
                  >
                    {titulo}
                    {columnaOrden === indice && (
                      <span style={{ marginLeft: 5 }}>
                        {direccionOrden === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                ))}

                {!!botones.length && botonesAlInicio && <th>{tituloBotones}</th>}

                {!!botones.length && !botonesAlInicio && <th>{tituloBotones}</th>}
              </tr>
            </thead>

            <tbody>
              {datosDeLaPaginaActual.filas.length ? (
                datosDeLaPaginaActual.filas.map((fila, indiceFila) => (
                  <tr key={indiceFila}>
                    {!!botones.length && botonesAlInicio && (
                      <td className="celda-responsive">
                        {botones.map((boton, indiceBoton) => (
                          <button
                            key={indiceBoton}
                            onClick={() => boton.accion?.(fila)}
                            className={boton.className || ""}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, ...boton.style }}
                          >
                            {boton.icono && <i className={`bi ${boton.icono}`} />}
                            {boton.texto || "Acción"}
                          </button>
                        ))}
                      </td>
                    )}

                    {campos.map((campo, indiceColumna) => (
                      <td
                        key={indiceColumna}
                        data-label={titulosCortos[indiceColumna] || campo}
                        className="celda-responsive"
                        style={estiloColumna(indiceColumna)}
                      >
                        {renderizadores[campo]
                          ? renderizadores[campo](fila[campo], fila)
                          : renderizarCelda(fila[campo])}
                      </td>
                    ))}

                    {!!botones.length && !botonesAlInicio && (
                      <td className="celda-responsive">
                        {botones.map((boton, indiceBoton) => (
                          <button
                            key={indiceBoton}
                            onClick={() => boton.accion?.(fila)}
                            className={boton.className || ""}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, ...boton.style }}
                          >
                            {boton.icono && <i className={`bi ${boton.icono}`} />}
                            {boton.texto || "Acción"}
                          </button>
                        ))}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={titulos.length + (botones.length ? 1 : 0)}
                    style={{ textAlign: "center", padding: 20 }}
                  >
                    {terminoBusqueda
                      ? `No se encontraron resultados para "${terminoBusqueda}"`
                      : "No hay datos disponibles"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <Paginacion />

        </div>
      </div>
    </div>
  );
};

export default TablaGenerica;